// frontend/src/context/CartContext.jsx

import React, { createContext, useContext, useReducer, useEffect } from 'react';
import toast from 'react-hot-toast';

const CartContext = createContext();

const cartReducer = (state, action) => {
    switch (action.type) {
        case 'ADD_ITEM': {
            const existing = state.items.find(
                i =>
                    i._id === action.payload._id &&
                    i.selectedSize === action.payload.selectedSize &&
                    i.selectedColor === action.payload.selectedColor
            );
            if (existing) {
                return {
                    ...state,
                    items: state.items.map(i =>
                        i._id === action.payload._id &&
                            i.selectedSize === action.payload.selectedSize &&
                            i.selectedColor === action.payload.selectedColor
                            ? { ...i, quantity: i.quantity + (action.payload.quantity || 1) }
                            : i
                    )
                };
            }
            return {
                ...state,
                items: [
                    ...state.items,
                    { ...action.payload, quantity: action.payload.quantity || 1 }
                ]
            };
        }

        case 'REMOVE_ITEM':
            return {
                ...state,
                items: state.items.filter((_, i) => i !== action.payload)
            };

        case 'UPDATE_QTY':
            return {
                ...state,
                items: state.items
                    .map((item, i) =>
                        i === action.payload.index
                            ? { ...item, quantity: action.payload.qty }
                            : item
                    )
                    .filter(item => item.quantity > 0)
            };

        case 'CLEAR_CART':
            return { ...state, items: [] };

        default:
            return state;
    }
};

// ─── Helper: resolve the effective price for a cart item ─────────────────────
// Uses discountPrice if it exists and is lower than price.
// This ensures cart totals, checkout totals, and order amounts are all correct.
const getEffectivePrice = (item) => {
    if (item.discountPrice && item.discountPrice < item.price) {
        return item.discountPrice;
    }
    return item.price;
};

export const CartProvider = ({ children }) => {
    const [state, dispatch] = useReducer(cartReducer, {
        items: JSON.parse(localStorage.getItem('raajdhani_cart') || '[]')
    });

    // Persist cart to localStorage on every change
    useEffect(() => {
        localStorage.setItem('raajdhani_cart', JSON.stringify(state.items));
    }, [state.items]);

    const addToCart = (product) => {
        if (product.isOutOfStock) {
            toast.error('This item is out of stock');
            return;
        }
        dispatch({ type: 'ADD_ITEM', payload: product });
        toast.success('Added to cart ✨', {
            style: {
                background: '#fbf9f5',
                color: '#630000',
                border: '1px solid #f2bdcd'
            }
        });
    };

    const removeFromCart = (index) => {
        dispatch({ type: 'REMOVE_ITEM', payload: index });
        toast.success('Removed from cart');
    };

    const updateQuantity = (index, qty) => {
        dispatch({ type: 'UPDATE_QTY', payload: { index, qty } });
    };

    const clearCart = () => dispatch({ type: 'CLEAR_CART' });

    // Total number of items (sum of quantities)
    const totalItems = state.items.reduce((sum, i) => sum + i.quantity, 0);

    // FIX: use discountPrice when available — was always using full price before
    const totalAmount = state.items.reduce(
        (sum, i) => sum + getEffectivePrice(i) * i.quantity,
        0
    );

    // Savings amount — useful for showing "You saved PKR X" in cart/checkout
    const totalSavings = state.items.reduce((sum, i) => {
        if (i.discountPrice && i.discountPrice < i.price) {
            return sum + (i.price - i.discountPrice) * i.quantity;
        }
        return sum;
    }, 0);

    return (
        <CartContext.Provider value={{
            cart: state.items,
            addToCart,
            removeFromCart,
            updateQuantity,
            clearCart,
            totalItems,
            totalAmount,
            totalSavings,   // bonus — use in CartDrawer/Checkout to show savings
            getEffectivePrice,
        }}>
            {children}
        </CartContext.Provider>
    );
};

export const useCart = () => useContext(CartContext);
